import type { UIEvent } from 'react';
import { useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { type ThemeSpaces } from '@frameio/vapor';
import { useSearchHistoryData } from '../../../shared/contexts';
import { useIsNLPEnabled } from '../../../shared/hooks';
import { useQuickSearchModalData } from '../../contexts';
import type { OnSearchSuggestionClick } from '../../types';
import {
  IntroBlock,
  RecentlyViewed,
  RecentSearches,
  SubmitFeedback,
  SuggestedSearches,
} from './components';
import { SuggestionsCarousel } from './components/SuggestionsCarousel';
import styles from './QuickSearchInitial.module.css';

const getGap = (isCompact: boolean, hasContent: boolean) => {
  if (!isCompact) return '24px';
  if (hasContent) return '16px';
  return 0;
};

type ContentTypes =
  | 'recentSearches'
  | 'recentlyViewed'
  | 'suggestedSearches'
  | null;

type ContentTop = Extract<
  ContentTypes,
  'recentSearches' | 'recentlyViewed' | 'suggestedSearches' | null
>;
type ContentBottom = Extract<
  ContentTypes,
  'suggestedSearches' | 'recentSearches' | null
>;

type SuggestionsCarouselProps = {
  cardWidth?: number;
  cardsPerSubset?: number;
};

type QuickSearchInitialProps = {
  /** The id of the list for accessibility */
  id: string;
  onClickSearchSuggestion: OnSearchSuggestionClick;
  onScroll: (evt: UIEvent<HTMLDivElement>) => void;
  // @todo (SRCH-1960): Hardcode paddingTop={12} and remove prop when v4.quick_search_tabbed_view is 100%
  paddingTop?: ThemeSpaces;
  suggestionsCarouselProps?: SuggestionsCarouselProps;
  isCompact?: boolean;
};
export const QuickSearchInitial = ({
  id,
  isCompact = false,
  onClickSearchSuggestion,
  onScroll,
  paddingTop,
  suggestionsCarouselProps,
}: QuickSearchInitialProps) => {
  const isNlpEnabled = useIsNLPEnabled();
  const { hasRecentProjects } = useQuickSearchModalData();
  const { hasSearchHistory } = useSearchHistoryData();

  /**
   *  returns a tuple representing the content type to display on the top and bottom of the grid
   */
  const [contentTop, contentBottom]: [ContentTop, ContentBottom] =
    useMemo(() => {
      if (hasSearchHistory && hasRecentProjects) {
        return ['recentlyViewed', 'recentSearches'];
      }

      if (hasRecentProjects && !hasSearchHistory) {
        return ['recentlyViewed', null];
      }

      if (hasSearchHistory && !hasRecentProjects) {
        return ['recentSearches', null];
      }

      return [null, null];
    }, [hasSearchHistory, hasRecentProjects]);

  const renderContent = useCallback(
    (content: ContentTypes) => {
      switch (content) {
        case 'recentSearches':
          return (
            <RecentSearches onClickRecentSearch={onClickSearchSuggestion} />
          );
        case 'recentlyViewed':
          return <RecentlyViewed />;
        default:
          return null;
      }
    },
    [onClickSearchSuggestion]
  );

  const hasContent = contentBottom !== null || contentTop !== null;

  return (
    <motion.div
      className={styles.quickSearchInitial}
      layout="preserve-aspect"
      onScroll={onScroll}
      style={{ '--padding-top': `${paddingTop}px` } as React.CSSProperties}
    >
      <div
        className={styles.contentWrapper}
        id={id}
        style={
          { '--gap': getGap(isCompact, hasContent) } as React.CSSProperties
        }
      >
        {!hasSearchHistory && <IntroBlock isCompact={isCompact} />}
        <div
          className={styles.contentGrid}
          data-has-single-child={contentBottom === null && contentTop === null}
        >
          {!isCompact && (
            <>
              {isNlpEnabled ? (
                <SuggestionsCarousel
                  cardsPerSubset={suggestionsCarouselProps?.cardsPerSubset}
                  cardWidth={suggestionsCarouselProps?.cardWidth}
                  onClickSuggestion={onClickSearchSuggestion}
                />
              ) : (
                <SuggestedSearches
                  onClickRecentSearch={onClickSearchSuggestion}
                />
              )}
            </>
          )}

          {renderContent(contentTop)}
          {renderContent(contentBottom)}
        </div>
      </div>
      <SubmitFeedback />
    </motion.div>
  );
};
